/**
 * Agrupa guardados costosos que se disparan con cada edición del usuario (p.ej. el snapshot de
 * toda la IndexedDB tras cada drag/resize en el editor): espera `delayMs` desde la última
 * solicitud, coalesce las que llegan en el medio, y si ya hay un guardado en vuelo encadena UNO
 * solo al final (nunca dos en paralelo, nunca se pierde la última edición). `flush()` guarda ya.
 *
 * Puro (solo `setTimeout`/`clearTimeout` globales): se testea con los fake timers de vitest.
 */
export interface CoalescedSaver {
  /** Pide un guardado "pronto": reinicia la espera de `delayMs`. */
  schedule(): void;

  /** Guarda ahora (cancela la espera). Resuelve cuando termina el guardado en curso. */
  flush(): Promise<void>;

  /** true si hay un guardado pendiente (esperando) o en vuelo. */
  pending(): boolean;
}

export function createCoalescedSaver(
  save: () => Promise<void>,
  delayMs: number,
  onError: (error: unknown) => void = () => {},
): CoalescedSaver {
  let timer: ReturnType<typeof setTimeout> | null = null;

  let inFlight: Promise<void> | null = null;

  let again = false;

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);

      timer = null;
    }
  };

  const flush = (): Promise<void> => {
    clearTimer();

    if (inFlight) {
      // Ya hay uno corriendo: que al terminar haga UNA pasada más con el estado final.
      again = true;

      return inFlight;
    }

    inFlight = (async () => {
      do {
        again = false;

        try {
          await save();
        } catch (error) {
          onError(error);
        }
      } while (again);
    })().finally(() => {
      inFlight = null;
    });

    return inFlight;
  };

  return {
    schedule() {
      clearTimer();

      timer = setTimeout(() => {
        timer = null;

        void flush();
      }, delayMs);
    },

    flush,

    pending() {
      return timer !== null || inFlight !== null;
    },
  };
}
